import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

type WishlistCtx = {
  slugs: Set<string>;
  isWished: (slug: string) => boolean;
  toggle: (slug: string) => Promise<void>;
  count: number;
  loading: boolean;
};

const Ctx = createContext<WishlistCtx | null>(null);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [slugs, setSlugs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Load wishlist whenever the user changes
  useEffect(() => {
    if (!user) {
      setSlugs(new Set());
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase
      .from("wishlist_items")
      .select("product_slug")
      .eq("user_id", user.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error(error);
          setSlugs(new Set());
        } else {
          setSlugs(new Set((data ?? []).map((r) => r.product_slug)));
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const isWished = useCallback((slug: string) => slugs.has(slug), [slugs]);

  const toggle = useCallback(
    async (slug: string) => {
      if (!user) {
        toast.message("Sign in to save favourites to your wishlist.");
        return;
      }
      const next = new Set(slugs);
      if (next.has(slug)) {
        next.delete(slug);
        setSlugs(next);
        const { error } = await supabase
          .from("wishlist_items")
          .delete()
          .eq("user_id", user.id)
          .eq("product_slug", slug);
        if (error) {
          toast.error("Could not remove from wishlist.");
          // revert
          setSlugs((s) => new Set(s).add(slug));
        } else {
          toast.message("Removed from wishlist.");
        }
      } else {
        next.add(slug);
        setSlugs(next);
        const { error } = await supabase
          .from("wishlist_items")
          .insert({ user_id: user.id, product_slug: slug });
        if (error) {
          toast.error("Could not add to wishlist.");
          setSlugs((s) => {
            const r = new Set(s);
            r.delete(slug);
            return r;
          });
        } else {
          toast.success("Saved to wishlist.");
        }
      }
    },
    [user, slugs],
  );

  const value = useMemo<WishlistCtx>(
    () => ({ slugs, isWished, toggle, count: slugs.size, loading }),
    [slugs, isWished, toggle, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWishlist() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
